import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { stringScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import { useAppSelector } from 'src/store';
import {
  useCreateConfigurationSettingsMutation,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import * as yup from 'yup';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { SalesRolePopup } from 'src/pages/ConfigurationPage/PersonaTab/PersonaForm/DynamicFields/RoleDescriptionField/SalesRoleField/SalesRolePopup/SalesRolePopup';
import { clsx } from 'clsx';
import { useGetAccountsQuery } from 'src/services/accountsService';
import { API_VERSION } from 'src/constants/api-routes';
import type {
  ConfigurationSettings,
  ConfigurationTemplateFields,
} from 'src/services/configurationService';
import type {
  PersonaInputData,
  PromptBuilderFormValues,
} from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import type { ConfigurationFormFieldsObject } from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import type { Workflow } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { getApiVersion } from 'src/utils/apiVersion';

// Keyed by the field's option keys.
export const ROLE_OPTIONS: Record<
  string,
  { id: number; title: string; description?: React.ReactNode }
> = {
  support: {
    id: 0,
    title: 'Support 🤝',
    description: 'Handle tickets, answer questions, troubleshoot.',
  },
  sales: {
    id: 1,
    title: 'Sales 💰',
    description: 'Engage prospects, highlight benefits, drive signups.',
  },
  custom: {
    id: 2,
    title: 'Custom 🌟',
  },
};

// Onboarding also keeps the default (escalation) workflow with the prompt builder's values.
type OnboardingPromptBuilderFormValues = PromptBuilderFormValues & {
  pf_default_workflow: Workflow | null;
};

const OnboardingStep4PrompBuilder = ({
  activeSettings,
  inputData,
  formFieldsObject,
  defaultTemplateFields,
}: {
  activeSettings: ConfigurationSettings | null;
  inputData: PersonaInputData | null;
  formFieldsObject: ConfigurationFormFieldsObject;
  defaultTemplateFields?: ConfigurationTemplateFields;
}) => {
  const customInputRef = useRef(null);
  const { bigin, project } = useAppSelector((s) => s.onboardingSlice);
  const { data: account } = useGetAccountsQuery();
  const [
    createConfigurationSettings,
    { data: createConfigurationSettingsData, isLoading: isLoadingCreateConfigurationSettings },
  ] = useCreateConfigurationSettingsMutation();
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();

  const navigate = useNavigate();
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;

  useLoaderWhile(isLoadingCreateConfigurationSettings, isLoadingUpdateConfigurationSettings);

  useEffect(() => {
    if (account?.company_name) {
      setValue('pf_company_name', account.company_name);
    }
  }, [account]);

  useEffect(() => {
    if (createConfigurationSettingsData || updateConfigurationSettingsData) {
      next();
    }
  }, [createConfigurationSettingsData, updateConfigurationSettingsData]);

  const back = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/${isV2 ? 2 : 3}?type=${ProductsType.customer}`, {
      replace: true,
    });
  };

  const next = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/5?type=${ProductsType.customer}`, { replace: true });
  };

  const { handleSubmit, control, reset, watch, setError, setValue } =
    useForm<OnboardingPromptBuilderFormValues>({
      defaultValues: {
        pf_ai_name: inputData ? inputData.pf_ai_name : project?.name || '',
        pf_company_name: inputData ? inputData.pf_company_name : bigin?.data?.companyName || '',
        pf_company_description: inputData ? inputData.pf_company_description : '',
        pf_role: inputData?.pf_role || formFieldsObject?.pf_role?.default_value?.value || 'support',
        pf_default_workflow:
          inputData?.pf_default_workflow ||
          formFieldsObject?.pf_default_workflow?.default_value?.value ||
          null,
        pf_role_custom: inputData?.pf_role_custom || '',
        pf_cta: inputData?.pf_cta || null,
        pf_icp: inputData?.pf_icp || null,
      },
      resolver: formResolver(
        yup.lazy((values) => {
          const baseSchema = {
            pf_role: stringScheme({
              required: formFieldsObject?.pf_role?.is_required || false,
            }).label('Role'),
            pf_role_custom: stringScheme({
              required: values.pf_role === 'custom',
            }).label('Role - Custom'),
          };

          return yup.object(baseSchema);
        }),
      ),
    });

  const role = watch('pf_role');
  const custom = watch('pf_role_custom');
  const cta = watch('pf_cta');
  const icp = watch('pf_icp');

  useEffect(() => {
    if (role && custom && role !== 'custom') {
      setValue('pf_role_custom', '');
    }
  }, [role, custom]);

  useEffect(() => {
    if (inputData) {
      reset({
        pf_ai_name: inputData ? inputData.pf_ai_name : project?.name || '',
        pf_company_name: inputData ? inputData.pf_company_name : bigin?.data?.companyName || '',
        pf_company_description: inputData ? inputData.pf_company_description : '',
        pf_role: inputData?.pf_role || formFieldsObject?.pf_role?.default_value?.value || 'support',
        pf_default_workflow:
          inputData?.pf_default_workflow ||
          formFieldsObject?.pf_default_workflow?.default_value?.value ||
          null,
        pf_role_custom: inputData?.pf_role_custom || '',
        pf_cta: inputData?.pf_cta || null,
        pf_icp: inputData?.pf_icp || null,
      });
    }
  }, [inputData]);

  const submit = (data: OnboardingPromptBuilderFormValues) => {
    if (!isV2 && role === 'sales' && (!cta || !icp)) {
      return setError('pf_role', {
        message: 'Sales role setup incomplete',
      });
    }

    if (activeSettings && !activeSettings.is_deployed) {
      const body = {
        input_data: {
          ...inputData,
          ...data,
          pf_role_custom: role === 'custom' ? data.pf_role_custom : null,
          pf_cta: role === 'sales' ? data.pf_cta : null,
          pf_icp: role === 'sales' ? data.pf_icp : null,
        },
      };

      updateConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id, body });
    } else {
      const body = {
        prompt: {
          template_id: defaultTemplateFields.id,
          input_data: data,
        },
      };

      createConfigurationSettings({ id: project.id, body });
    }
  };

  const addSalesRole = (submitData: Pick<PromptBuilderFormValues, 'pf_icp' | 'pf_cta'>) => {
    setValue('pf_icp', submitData.pf_icp);
    setValue('pf_cta', submitData.pf_cta);
    setValue('pf_role', 'sales');
  };

  return (
    <>
      <div
        className={
          'flex flex-col w-full gap-[5px] px-[50px] py-1 h-full items-center overflow-y-auto'
        }
      >
        <form
          id={'onboarding-step-4'}
          className={'flex flex-col w-full gap-5'}
          onSubmit={handleSubmit(submit)}
        >
          <div className={'flex flex-col gap-2.5'}>
            <Controller
              control={control}
              name="pf_role"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <RadioGroup
                  onValueChange={(val) => {
                    onChange(val);

                    if (val === 'custom') {
                      customInputRef?.current?.focus();
                    }
                  }}
                  value={value}
                  className="flex flex-col mt-5 mb-2.5 gap-5"
                >
                  {formFieldsObject?.pf_role?.options?.map((option) => {
                    const item = {
                      ...ROLE_OPTIONS[option.key],
                      ...option,
                    };

                    if (isV2 && item.value === 'custom') {
                      return null;
                    }

                    return (
                      <div key={`${item.id}-${item.key}`} className={'flex items-start gap-2.5'}>
                        <RadioGroupItem className={'mt-1'} value={item.value} id={item.value} />
                        <label htmlFor={item.value} className="cursor-pointer">
                          <p className="text-sm font-semibold text-ebony opacity-80 hover:opacity-100 transition-colors">
                            {item.title || item.key}
                          </p>
                          {item.description && (
                            <p className={'text-sm text-ebony_opacity_50 font-medium'}>
                              {item.description}
                            </p>
                          )}
                          <div className={'flex flex-row gap-2.5 mt-1'}>
                            {item.value === 'sales' && (
                              <>
                                <SalesRolePopup
                                  data={inputData}
                                  watch={watch}
                                  addSalesRole={addSalesRole}
                                  roleClickValue={role}
                                  titleDescription1={
                                    'Describe your customers’ main pain points and how your solution addresses them — the AI will use this to tailor its sales conversations.'
                                  }
                                  titleDescription2={
                                    'Describe your customers’ main pain points and how your solution addresses them — the AI will use this to tailor its sales conversations.'
                                  }
                                  titleDescription3={
                                    "Set up call-to-action responses (e.g., “Let's schedule a demo session” or “Ready to kick off a free trial?”) so the AI knows how to react when buying intent is detected."
                                  }
                                  triggerClassName={clsx(
                                    role !== 'sales' && 'pointer-events-none text-ebony_opacity_50',
                                  )}
                                />
                                {error && <p className={'text-error text-xs'}>{error.message}</p>}
                              </>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </RadioGroup>
              )}
            />
            {role === 'custom' && (
              <Controller
                control={control}
                name={'pf_role_custom'}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className={'flex flex-col w-full relative'}>
                    <Input
                      ref={customInputRef}
                      readonly={role !== 'custom'}
                      textareaRows={3}
                      type={'textarea'}
                      autoFocus={true}
                      placeholder={
                        'Provide your AI agent’s role here. For example: Customer Support Specialist who assists users with troubleshooting.'
                      }
                      invalidMessage={error?.message}
                      value={value}
                      isValid={true}
                      onChange={onChange}
                    />
                    {error?.message && (
                      <div
                        className={
                          'absolute top-[30%] left-[calc(100%+8px)] border border-[#C11A45B2] text-[#C11A45B2] rounded-sm-5 text-xs px-3 py-[9px]'
                        }
                      >
                        <div className="absolute left-0 top-1/4 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-white border-l border-b border-[#C11A45B2]"></div>
                        {error?.message}
                      </div>
                    )}
                  </div>
                )}
              />
            )}
          </div>
        </form>
      </div>
      <div className={'mt-5 flex items-center justify-center gap-5'}>
        <div className={'flex flex-col w-full items-center justify-center gap-5'}>
          <Button form={'onboarding-step-4'} className="w-full py-6 font-semibold">
            <p>Continue</p>
          </Button>
          <button
            type="button"
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </button>
        </div>
      </div>
    </>
  );
};

export default OnboardingStep4PrompBuilder;
